import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

/**
 * Error types for categorization and appropriate handling
 */
export enum ErrorType {
  NETWORK = 'network',
  RENDER = 'render',
  ASYNC = 'async',
  CHUNK_LOAD = 'chunk_load',
  UNKNOWN = 'unknown',
}

/**
 * Error metadata collected during error boundary catch
 */
export interface ErrorMetadata {
  errorType: ErrorType;
  componentStack?: string;
  digest?: string;
  timestamp: string;
  url: string;
  userAgent: string;
  errorId: string;
}

interface Props {
  /** Child components to render */
  children: ReactNode;
  /** Custom fallback UI to render on error */
  fallback?: ReactNode;
  /** Render prop for custom fallback with error context */
  fallbackRender?: (props: FallbackProps) => ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo, metadata: ErrorMetadata) => void;
  /** Callback when error is reset */
  onReset?: () => void;
  /** Additional context to include in error logs */
  context?: Record<string, unknown>;
  /** Unique name for this error boundary instance */
  name?: string;
  /** Whether to show error details in development mode */
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorMetadata?: ErrorMetadata;
}

/**
 * Props passed to fallback render function
 */
export interface FallbackProps {
  error: Error;
  errorInfo?: ErrorInfo;
  errorMetadata?: ErrorMetadata;
  resetError: () => void;
}

/**
 * Generates a unique error ID for tracking
 */
function generateErrorId(): string {
  return `err_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Categorizes error type based on error characteristics
 */
function categorizeError(error: Error): ErrorType {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  // Check for chunk loading errors (code splitting)
  if (
    message.includes('loading chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading css chunk')
  ) {
    return ErrorType.CHUNK_LOAD;
  }

  // Check for network errors
  if (
    name === 'networkerror' ||
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('failed to fetch') ||
    message.includes('net::') ||
    message.includes('cors')
  ) {
    return ErrorType.NETWORK;
  }

  // Check for async errors
  if (
    name === 'promiserejectionevent' ||
    message.includes('promise') ||
    message.includes('async')
  ) {
    return ErrorType.ASYNC;
  }

  // Default to render error for React component errors
  if (
    message.includes('render') ||
    message.includes('component') ||
    message.includes('undefined is not') ||
    message.includes('null is not') ||
    message.includes('cannot read property')
  ) {
    return ErrorType.RENDER;
  }

  return ErrorType.UNKNOWN;
}

/**
 * Default fallback UI component
 */
function DefaultFallback({
  error,
  errorMetadata,
  resetError,
  showDetails = false,
}: FallbackProps & { showDetails?: boolean }) {
  const isDev = import.meta.env.MODE === 'development';
  const { t } = useTranslation('errors');

  return (
    <main
      aria-labelledby="error-boundary-title"
      className="min-h-screen bg-surface-light dark:bg-surface-dark flex items-center justify-center px-4 font-sans transition-colors duration-200"
    >
      <div className="max-w-md w-full text-center animate-in fade-in zoom-in duration-700">
        {/* Error Icon */}
        <div className="mb-8">
          <div className="mx-auto w-24 h-24 bg-state-danger-light dark:bg-state-danger-dark rounded-3xl flex items-center justify-center shadow-2xl shadow-state-danger-light/20 dark:shadow-none">
            <svg
              aria-hidden="true"
              className="w-12 h-12 text-white dark:text-on-accent-dark"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Error Message */}
        <div className="relative z-10">
          <h1
            id="error-boundary-title"
            className="text-3xl font-black text-slate-900 dark:text-white mb-3 tracking-tight"
          >
            {t('generic.title')}
          </h1>
          <p className="text-text-muted-light dark:text-text-muted-dark font-medium mb-6 leading-relaxed">
            {t('generic.description')}
          </p>

          {/* Error ID for support reference */}
          {errorMetadata?.errorId && (
            <div className="bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark p-4 rounded-xl mb-6">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-text-subtle-light dark:text-text-subtle-dark mb-2">
                {t('generic.errorReference')}
              </p>
              <code className="text-xs font-mono text-slate-600 dark:text-slate-300 break-all">
                {errorMetadata.errorId}
              </code>
            </div>
          )}

          {/* Error Details (Development mode or when showDetails is true) */}
          {(isDev || showDetails) && error && (
            <div className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 p-4 rounded-xl mb-6 text-left">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-state-danger-light dark:text-state-danger-dark mb-2">
                {t('generic.errorDetails')}
              </p>
              <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark mb-2">
                {error.name}
              </p>
              <p className="text-xs text-state-danger-light dark:text-state-danger-dark mb-2">
                {error.message}
              </p>
              {error.stack && (
                <pre className="text-[10px] text-state-danger-light dark:text-state-danger-dark overflow-auto max-h-32 whitespace-pre-wrap">
                  {error.stack}
                </pre>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={resetError}
              className="inline-flex min-h-11 items-center justify-center px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold uppercase tracking-wide rounded-xl shadow-lg transition-all active:scale-95"
              data-testid="error-boundary-retry"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {t('generic.retry')}
            </button>
            <Link
              to="/dashboard"
              onClick={resetError}
              className="inline-flex min-h-11 items-center justify-center px-6 py-3 bg-surface-2-light dark:bg-surface-2-dark hover:bg-surface-border-light dark:hover:bg-surface-border-dark text-slate-700 dark:text-slate-200 text-sm font-bold uppercase tracking-wide rounded-xl transition-all active:scale-95"
              data-testid="error-boundary-dashboard"
            >
              <svg
                aria-hidden="true"
                className="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              {t('500.dashboardLink')}
            </Link>
          </div>

          {/* Support Link */}
          <div className="mt-8 pt-6 border-t border-surface-border-light dark:border-surface-border-dark">
            <p className="flex flex-wrap items-center justify-center gap-x-1 text-xs text-text-subtle-light dark:text-text-subtle-dark">
              {t('500.supportHeading')}{' '}
              <a
                href="mailto:support@pgkb.com"
                className="inline-flex min-h-11 items-center text-brand-600 dark:text-brand-400 hover:underline font-semibold"
              >
                {t('500.supportChannel')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors in child component tree, logs them to monitoring,
 * and displays a user-friendly fallback UI instead of crashing the whole app.
 *
 * Features:
 * - Error categorization (network, render, async, chunk loading)
 * - Structured logging with full context
 * - Reset/retry functionality
 * - Custom fallback UI support
 * - Error ID generation for support reference
 * - Development mode error details display
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // With custom fallback
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // With fallback render prop
 * <ErrorBoundary
 *   fallbackRender={({ error, resetError }) => (
 *     <div>
 *       <p>Error: {error.message}</p>
 *       <button onClick={resetError}>Retry</button>
 *     </div>
 *   )}
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // With error callback and context
 * <ErrorBoundary
 *   name="UserProfile"
 *   context={{ userId: '123' }}
 *   onError={(error, info, metadata) => {
 *     // Send to external monitoring service
 *     sendToSentry(error, metadata);
 *   }}
 *   onReset={() => {
 *     // Clear cached data on retry
 *     queryClient.invalidateQueries();
 *   }}
 * >
 *   <UserProfile />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorType = categorizeError(error);
    const errorId = generateErrorId();

    // Create error metadata
    const errorMetadata: ErrorMetadata = {
      errorType,
      componentStack: errorInfo.componentStack || undefined,
      digest: errorInfo.digest || undefined,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorId,
    };

    // Update state with error info
    this.setState({ errorInfo, errorMetadata });

    // Log error using structured logger with full context
    logger.error('React ErrorBoundary caught an error', error, {
      ...errorMetadata,
      errorBoundary: this.props.name || 'ErrorBoundary',
      errorType,
      ...this.props.context,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo, errorMetadata);
    }

    // For chunk loading errors, try to recover by reloading the page
    if (errorType === ErrorType.CHUNK_LOAD) {
      logger.info('Chunk loading error detected, page reload may help', {
        errorId,
        errorType,
      });
    }
  }

  /**
   * Reset the error state and try rendering children again
   */
  resetError = () => {
    logger.info('ErrorBoundary reset triggered', {
      errorBoundary: this.props.name || 'ErrorBoundary',
      previousError: this.state.error?.message,
      errorId: this.state.errorMetadata?.errorId,
    });

    // Call onReset callback if provided
    if (this.props.onReset) {
      this.props.onReset();
    }

    // Reset the error state
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorMetadata: undefined,
    });
  };

  render() {
    const { hasError, error, errorInfo, errorMetadata } = this.state;
    const { children, fallback, fallbackRender, showDetails } = this.props;

    if (hasError && error) {
      // Use fallbackRender prop if provided (render prop pattern)
      if (fallbackRender) {
        return fallbackRender({
          error,
          errorInfo,
          errorMetadata,
          resetError: this.resetError,
        });
      }

      // Use static fallback if provided
      if (fallback) {
        return fallback;
      }

      // Use default fallback UI
      return (
        <DefaultFallback
          error={error}
          errorInfo={errorInfo}
          errorMetadata={errorMetadata}
          resetError={this.resetError}
          showDetails={showDetails}
        />
      );
    }

    return children;
  }
}

/**
 * Higher-order component to wrap a component with an ErrorBoundary
 * Usage: const SafeComponent = withErrorBoundary(MyComponent, { name: 'MyComponent' });
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
): React.FC<P> {
  const WithErrorBoundary: React.FC<P> = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithErrorBoundary;
}

export default ErrorBoundary;
